import { isEqual } from 'lodash';
import {
  ReactNode,
  useState,
  useCallback,
  useEffect,
  CSSProperties,
  useMemo,
  useLayoutEffect,
} from 'react';

import { generateUUID } from '@/lib/utils';

import { ToastState, AddToastFn, ToastData, Toast } from '.';
import { ToastContext } from './toast-context';

import styles, { exitTransitionDuration } from './toast.module.scss';

const MaxStack = 3;
const ToastDuration = 5500;
const ExitDelay = Number(exitTransitionDuration) / 2;

export function ToastContextProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [toastHeights, setToastHeights] = useState<number[]>([]);

  const cleanupToast = useCallback(() => {
    const now = Date.now();

    setToasts((currentToasts) => {
      const updatedToasts = currentToasts.map((toast) => {
        const isExpired = now - toast.toastedAt.getTime() >= ToastDuration;
        return isExpired && !toast.isExiting ? { ...toast, isExiting: true } : toast;
      });

      return !isEqual(updatedToasts, currentToasts) ? updatedToasts : currentToasts;
    });

    setTimeout(() => {
      setToasts((currentToasts) => {
        const filteredToasts = currentToasts.filter(
          (toast) => !toast.isExiting || now - toast.toastedAt.getTime() < ToastDuration,
        );

        return !isEqual(filteredToasts, currentToasts) ? filteredToasts : currentToasts;
      });
    }, ExitDelay);
  }, []);

  useEffect(() => {
    if (toasts.length > 0 && !isExpanded) {
      const interval = setInterval(cleanupToast, 1000);
      return () => clearInterval(interval);
    }
  }, [toasts.length, isExpanded, cleanupToast]);

  const addToast: AddToastFn = useCallback(
    (toast: ToastData) =>
      setToasts((toasts) => [
        { ...toast, id: generateUUID(), toastedAt: new Date(), isExiting: false },
        ...toasts,
      ]),
    [],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((currentToasts) =>
      currentToasts.map((toast) => (toast.id === id ? { ...toast, isExiting: true } : toast)),
    );

    setTimeout(() => {
      setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id));
    }, ExitDelay);
  }, []);

  const getToastStyle = (index: number, total: number) => {
    const position = total - 1 - index;
    if (position >= MaxStack) {
      return {
        opacity: 0,
        visibility: 'hidden',
        position: 'fixed',
        pointerEvents: 'none',
      } as CSSProperties;
    }

    if (isExpanded || position === 0) {
      return {};
    }

    const scale = 1 - 0.02 * position;
    const previousToastHeights = toastHeights
      .slice()
      .reverse()
      .slice(1, position)
      .reduce((acc, height) => acc + height, 0);
    const offset = previousToastHeights + (toastHeights[visibleToasts.length - 1 - position] || 0);

    return {
      transform: `translate3d(0, ${Math.round(offset)}px, 0) scale(${scale})`,
    };
  };

  const visibleToasts = useMemo(() => toasts.slice(0, MaxStack + 1), [toasts]);

  useLayoutEffect(() => {
    const toasts = Array.from(document.querySelectorAll(`.${styles.toast}`)) as HTMLElement[];
    const heights = toasts.map((el) => Math.round(el.offsetHeight));
    setToastHeights(heights);
  }, [toasts]);

  return (
    <ToastContext.Provider value={addToast}>
      <div className={styles.toasts} onMouseLeave={() => setIsExpanded(false)}>
        {visibleToasts
          .slice()
          .reverse()
          .map((toast, idx) => (
            <Toast
              idx={idx}
              key={toast.id}
              stackLength={visibleToasts.length}
              onRemove={() => removeToast(toast.id)}
              onExpand={() => setIsExpanded(true)}
              style={getToastStyle(idx, visibleToasts.length)}
              {...toast}
            />
          ))}
      </div>
      {children}
    </ToastContext.Provider>
  );
}
